import { useLocation, Route, Routes } from "react-router-dom";
import { Box } from "@mui/material";
import { retrievePath } from "lib/routing";
import { AppRouteRendererProps } from "./AppRouteRenderer.types";
import { Path } from "lib/routing/paths/paths.types";
import { sxStyles } from "./AppRouteRenderer.styles";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import { ROUTES } from "../GlobalRoutes/GlobalRoutes";
import ErrorModal from "./ErrorModal/ErrorModal";
import { containsDynamicBackButtonConfigPath } from "../customizedRoutes/routesWithDynamicBackButtonDisplayConfig";
import { useEffect } from "react";
import CustomOidcSecure from "lib/components/auth/CustomOidcSecure/CustomOidcSecure";

/**
 * The BrowserRouter for the entire application. Mount this component in order
 * to register all of the application's routes provided by the routing module.
 */
const AppRouteRenderer = ({
  openFlagErrorModal,
  messageErrorModal,
  customizedErrorModalButtonText,
  customizedNavBarDisplayName,
  closeErrorModal,
}: AppRouteRendererProps) => {
  const classes = sxStyles();
  const { pathname } = useLocation();

  const path = retrievePath(pathname);
  const title = path?.displayName
    ? path?.displayName
    : customizedNavBarDisplayName;

  // The following useEffect is used to set the title of the page
  // dynamically for mobile to grab and use on their navbar
  useEffect(() => {
    document.title = title ?? "";
  }, [title]);

  return (
    <>
      <ScrollToTop />
      <Box sx={classes.noNav}>
        <Routes>
          {ROUTES.map(({ path, component: Component }) => {
            return (
              <Route
                key={path}
                path={path}
                element={
                  <CustomOidcSecure>
                    <Component />
                  </CustomOidcSecure>
                }
              />
            );
          })}
        </Routes>

        <ErrorModal
          open={openFlagErrorModal}
          path={path}
          errorMessage={messageErrorModal}
          customizedButtonText={customizedErrorModalButtonText}
          closeErrorModal={closeErrorModal}
        />
      </Box>
    </>
  );
};

// ======================
// Helper functions
// ======================

/**
 * This function determines whether to show a back button based on certain conditions.
 * @param {Path | null} path - The `path` parameter is of type `Path` or `null`, and it represents the
 * current path in the application.
 * @param {boolean} shouldShowBackButtonOnEntry - `shouldShowBackButtonOnEntry` is a boolean flag
 * indicating whether the back button should be shown on entry.
 * @param {boolean | null} shouldShowBackOnEntryHijack - The `shouldShowBackOnEntryHijack` parameter is
 * a boolean value that determines whether the back button should be shown on entry hijack. If it is
 * set to `true`, the back button should be shown on entry hijack. If it is set to `false`, the back
 * button
 * @returns either `shouldShowBackButtonOnEntry`, `shouldShowBackOnEntryHijack`, or
 * `path?.allowGoBackInHistory` based on the conditions provided in the function.
 */
const shouldShowBackButton = (
  path: Path | null,
  shouldShowBackButtonOnEntry: boolean,
  shouldShowBackOnEntryHijack: boolean | null,
) => {
  if (containsDynamicBackButtonConfigPath(path?.path || "") === true) {
    return shouldShowBackButtonOnEntry;
  }

  if (shouldShowBackOnEntryHijack !== null) {
    return shouldShowBackOnEntryHijack;
  }

  return path?.allowGoBackInHistory;
};

export default AppRouteRenderer;
export const exportForTesting = { shouldShowBackButton };
